import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { StaffFeesPendingReportService, PendingFeeClassRow } from '../services/staff-fees-pending-report.service';
import { StaffFeesOverdueReportService, OverdueFeeClassRow } from '../services/staff-fees-overdue-report.service';
import { StaffFeesReportStudentsNavState } from '../fees-report-students/staff-fees-report-students.component';
import { StaffFeesReportOverdueStudentsNavState } from '../fees-report-overdue-students/staff-fees-report-overdue-students.component';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;

type SortKey = 'dueFees' | 'studentCount' | null;

export interface StaffFeesReportNavState {
  reportType: 'overdue' | 'pending';
}

@Component({
  selector: 'app-staff-fees-report',
  templateUrl: './staff-fees-report.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-fees-report.component.css']
})
export class StaffFeesReportComponent implements OnInit {
  reportType: 'overdue' | 'pending' = 'overdue';

  staffId: number | string | null = null;
  currencySymbol = '';

  // ---------- Pending tab (class-summary) — backed by the app's "overdue"
  // endpoints; see apiConstant.ts naming note. ----------
  pendingClassRows: PendingFeeClassRow[] = [];
  fetchingPendingClassListData = true;
  refreshingPendingList = false;
  pendingLastUpdatedOn: string | null = null;
  pendingSortKey: SortKey = null;
  pendingSortAscending = false;

  // ---------- Overdue tab (class-summary) — a separate parallel flow
  // backed by the app's "pending"-named endpoints (see apiConstant.ts
  // naming note — the inversion is deliberate/pre-existing, not a mistake
  // introduced here). ----------
  overdueClassRows: OverdueFeeClassRow[] = [];
  fetchingOverdueClassListData = true;
  refreshingOverdueList = false;
  overdueLastUpdatedOn: string | null = null;
  overdueSortKey: SortKey = null;
  overdueSortAscending = false;

  constructor(
    private staffFeesPendingReportService: StaffFeesPendingReportService,
    private staffFeesOverdueReportService: StaffFeesOverdueReportService,
    private staffSession: StaffSessionService,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
    this.currencySymbol = info?.currencySymbol || staffDetails?.currencyType || '';

    // Coming back from either flow's student-list/student-history screens
    // is itself a forward router.navigate, not a browser "back" — without
    // this, this component always remounts defaulting to reportType
    // 'overdue', discarding whichever tab (Pending or Overdue) the user
    // actually came from.
    const state = history.state as StaffFeesReportNavState;
    if (state?.reportType) {
      this.reportType = state.reportType;
    }

    this.loadOverdueClassData();
    this.loadPendingClassData();
  }

  setType(t: 'overdue' | 'pending') {
    this.reportType = t;
  }

  formatAmount(value: number): string {
    return `${this.currencySymbol}${(value || 0).toLocaleString('en-IN')}`;
  }

  // ==================== Pending tab ====================

  private async loadPendingClassData() {
    if (!this.staffId) { this.fetchingPendingClassListData = false; return; }
    try {
      const response = await firstValueFrom(this.staffFeesPendingReportService.getOverdueClassList(this.staffId));
      this.pendingClassRows = response?.status ? (response.body?.reportClassData || []) : [];
      this.pendingLastUpdatedOn = response?.body?.lastUpdatedOn || null;
    } catch {
      this.pendingClassRows = [];
    } finally {
      this.fetchingPendingClassListData = false;
    }
  }

  async refreshPendingList() {
    if (!this.staffId) { return; }
    if (this.fetchingPendingClassListData || this.refreshingPendingList) {
      const alreadyRefreshingTranslation = await firstValueFrom(this.translate.get('alreadyRefreshingPleaseWait'));
      customToastr(alreadyRefreshingTranslation, 'warning');
      return;
    }
    this.refreshingPendingList = true;
    try {
      const response = await firstValueFrom(this.staffFeesPendingReportService.refreshFeesData(this.staffId));
      if (response?.status) {
        const message = response.body?.message
          || await firstValueFrom(this.translate.get('feeDataRefreshedSuccessfully'));
        customToastr(message, 'success');
        await this.loadPendingClassData();
      } else {
        const errorMessage = await firstValueFrom(this.translate.get('somethingWentWrong'));
        customToastr(errorMessage, 'error');
      }
    } catch {
      const errorMessage = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(errorMessage, 'error');
    } finally {
      this.refreshingPendingList = false;
    }
  }

  pendingClassLabel(c: PendingFeeClassRow): string {
    return `${c.classNumber}-${c.classDivision}`;
  }

  get pendingLastUpdatedLabel(): string {
    return this.pendingLastUpdatedOn ? moment(this.pendingLastUpdatedOn).fromNow() : '';
  }

  // Selecting a sort column resets the other's state — only one column is
  // ever shown as actively sorted, matching the mobile app's mutually
  // exclusive sort-bar behaviour.
  setPendingSort(key: SortKey) {
    this.pendingSortAscending = this.pendingSortKey === key ? !this.pendingSortAscending : false;
    this.pendingSortKey = key;
    const field = key === 'dueFees' ? 'activeStudentTotalPendingAmount' : 'activeStudentPendingCount';
    const direction = this.pendingSortAscending ? 1 : -1;
    this.pendingClassRows = [...this.pendingClassRows].sort((a, b) => (a[field] - b[field]) * direction);
  }

  // classId travels as a route param; classNumber/classDivision travel via
  // router state (same pattern as staff-fees.component.ts's navigation to
  // fees-monthly) so the next screen can show the class name immediately
  // without waiting on its own API call.
  openPendingClass(c: PendingFeeClassRow) {
    const state: StaffFeesReportStudentsNavState = {
      classNumber: c.classNumber,
      classDivision: c.classDivision
    };
    this.router.navigate(['/staff/fees-report/class', c.classId], { state });
  }

  // ==================== Overdue tab ====================

  private async loadOverdueClassData() {
    if (!this.staffId) { this.fetchingOverdueClassListData = false; return; }
    try {
      const response = await firstValueFrom(this.staffFeesOverdueReportService.getPendingClassList(this.staffId));
      this.overdueClassRows = response?.status ? (response.body?.reportClassData || []) : [];
      this.overdueLastUpdatedOn = response?.body?.lastUpdatedOn || null;
    } catch {
      this.overdueClassRows = [];
    } finally {
      this.fetchingOverdueClassListData = false;
    }
  }

  async refreshOverdueList() {
    if (!this.staffId) { return; }
    if (this.fetchingOverdueClassListData || this.refreshingOverdueList) {
      const alreadyRefreshingTranslation = await firstValueFrom(this.translate.get('alreadyRefreshingPleaseWait'));
      customToastr(alreadyRefreshingTranslation, 'warning');
      return;
    }
    this.refreshingOverdueList = true;
    try {
      const response = await firstValueFrom(this.staffFeesOverdueReportService.refreshFeesDataOverdue(this.staffId));
      if (response?.status) {
        const message = response.body?.message
          || await firstValueFrom(this.translate.get('feeDataRefreshedSuccessfully'));
        customToastr(message, 'success');
        await this.loadOverdueClassData();
      } else {
        const errorMessage = await firstValueFrom(this.translate.get('somethingWentWrong'));
        customToastr(errorMessage, 'error');
      }
    } catch {
      const errorMessage = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(errorMessage, 'error');
    } finally {
      this.refreshingOverdueList = false;
    }
  }

  overdueClassLabel(c: OverdueFeeClassRow): string {
    return `${c.classNumber}-${c.classDivision}`;
  }

  get overdueLastUpdatedLabel(): string {
    return this.overdueLastUpdatedOn ? moment(this.overdueLastUpdatedOn).fromNow() : '';
  }

  setOverdueSort(key: SortKey) {
    this.overdueSortAscending = this.overdueSortKey === key ? !this.overdueSortAscending : false;
    this.overdueSortKey = key;
    const field = key === 'dueFees' ? 'activeStudentTotalOverdueAmount' : 'activeStudentOverdueCount';
    const direction = this.overdueSortAscending ? 1 : -1;
    this.overdueClassRows = [...this.overdueClassRows].sort((a, b) => (a[field] - b[field]) * direction);
  }

  openOverdueClass(c: OverdueFeeClassRow) {
    const state: StaffFeesReportOverdueStudentsNavState = {
      classNumber: c.classNumber,
      classDivision: c.classDivision
    };
    this.router.navigate(['/staff/fees-overdue-report/class', c.classId], { state });
  }
}
